<template>
  <div class="app-shell">
    <Menubar v-if="auth.isLoggedIn" :model="menuItems" class="app-nav">
      <template #start>
        <span class="app-logo" @click="goHome()">🎭 <span class="logo-text">cultuurdealer</span></span>
      </template>
      <template #end>
        <div class="nav-end">
          <div v-if="auth.isImpersonating" class="impersonation-banner">
            <i class="pi pi-eye" />
            <span class="impersonation-name">{{ $t('impersonate.banner', { name: auth.user?.displayName }) }}</span>
            <Button
              :label="$t('impersonate.stop')"
              icon="pi pi-times"
              size="small"
              severity="warn"
              outlined
              class="impersonation-stop-btn"
              @click="auth.stopImpersonating()"
            />
          </div>
          <Select
            v-if="auth.isRealAdmin && route.params.poolId && poolMembers.length > 0"
            v-model="viewAsUserId"
            :options="poolMembers"
            option-label="displayName"
            option-value="id"
            :placeholder="$t('impersonate.viewAs')"
            show-clear
            class="view-as-select"
            style="width: 160px; font-size: 0.875rem"
            @change="onViewAsChange"
          />
          <LanguageSwitcher />
          <Button
            v-if="!auth.isImpersonating"
            :label="auth.user?.displayName"
            icon="pi pi-user"
            text
            class="nav-account-btn"
            @click="router.push('/account')"
          />
          <Button
            :label="$t('app.logout')"
            icon="pi pi-sign-out"
            text
            severity="secondary"
            class="nav-logout-btn"
            @click="auth.logout()"
          />
        </div>
      </template>
    </Menubar>

    <main class="app-content">
      <RouterView :key="viewKey" />
    </main>

    <Toast />
    <ConfirmDialog />
    <OnboardingDialog v-if="showOnboarding" :visible="true" @close="closeOnboarding" />
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { usePoolStore } from '@/stores/pool'
import Menubar from 'primevue/menubar'
import Button from 'primevue/button'
import Select from 'primevue/select'
import Toast from 'primevue/toast'
import ConfirmDialog from 'primevue/confirmdialog'
import LanguageSwitcher from '@/components/LanguageSwitcher.vue'
import OnboardingDialog from '@/components/OnboardingDialog.vue'

const router = useRouter()
const route = useRoute()
const auth = useAuthStore()
const poolStore = usePoolStore()
const { t } = useI18n()

const showOnboarding = ref(false)
const onboardingDismissed = ref(false)

const viewAsUserId = ref(auth.isImpersonating ? auth.user?.id : null)

const viewKey = computed(() => `${route.fullPath}:${auth.user?.id ?? ''}`)

const poolMembers = computed(() => {
  const users = poolStore.currentPool?.users
  if (!users?.length) return []
  const realAdminId = auth.isImpersonating ? auth.realUser?.id : auth.user?.id
  return [...users]
    .filter(m => m.id !== realAdminId)
    .sort((a, b) => a.displayName.localeCompare(b.displayName))
})

watch([() => auth.isImpersonating, () => auth.user?.id], () => {
  viewAsUserId.value = auth.isImpersonating ? auth.user?.id : null
})

async function onViewAsChange(event) {
  if (event.value) {
    await auth.impersonate(event.value)
  } else {
    await auth.stopImpersonating()
  }
}

// Show the dialog only once the user has fully landed on a non-login page.
// Watching both needsOnboarding and route.path handles two cases:
//   1. Already authenticated on page load (immediate hit, non-login path)
//   2. Just logged in via /login — needsOnboarding becomes true while still on
//      /login, so we skip it there; the path watch fires again when the router
//      completes navigation to '/', triggering the dialog then.
// onboardingDismissed prevents the path watch from re-opening the dialog after
// the user has clicked "Later".
watch(
  [() => auth.needsOnboarding, () => route.path],
  ([needed]) => {
    if (needed && route.path !== '/login' && !onboardingDismissed.value) {
      showOnboarding.value = true
    }
  },
  { immediate: true }
)

watch(() => auth.isLoggedIn, (loggedIn) => {
  if (!loggedIn) {
    showOnboarding.value = false
    onboardingDismissed.value = false
  }
})

function closeOnboarding() {
  showOnboarding.value = false
  onboardingDismissed.value = true
}

function goHome() {
  const pool = poolStore.currentPool
  if (!pool) { router.push('/'); return }
  if (pool.phase === 'PREPARATION') router.push(`/pools/${pool.id}/prepare`)
  else if (pool.phase === 'PURCHASING') router.push(`/pools/${pool.id}/purchase`)
  else router.push(`/pools/${pool.id}/my-tickets`)
}

const menuItems = computed(() => {
  const items = []
  if (auth.isRealAdmin) {
    items.push({
      label: t('app.admin'),
      icon: 'pi pi-cog',
      items: [
        { label: t('admin.users'), icon: 'pi pi-users', command: () => router.push('/admin/users') },
        { label: t('admin.pools'), icon: 'pi pi-th-large', command: () => router.push('/admin/pools') }
      ]
    })
  }
  if (route.params.poolId && poolStore.currentPool && poolStore.isManagement) {
    items.push({
      label: t('myTickets.navLabel'),
      icon: 'pi pi-ticket',
      command: () => router.push(`/pools/${poolStore.currentPool.id}/my-tickets`)
    })
  }
  if (auth.isRealAdmin && route.params.poolId && poolStore.currentPool && poolStore.isManagement) {
    items.push({
      label: t('tickets.management'),
      icon: 'pi pi-wrench',
      command: () => router.push(`/pools/${poolStore.currentPool.id}/tickets`)
    })
  }
  if (auth.isRealAdmin && route.params.poolId && poolStore.currentPool) {
    items.push({
      label: t('bookingOverview.button'),
      icon: 'pi pi-list-check',
      command: () => router.push(`/pools/${poolStore.currentPool.id}/booking-overview`)
    })
  }
  return items
})
</script>

<style>
* { box-sizing: border-box; }

body {
  margin: 0;
  font-family: var(--p-font-family);
  background: var(--p-surface-50);
  color: var(--p-text-color);
}

.app-shell {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.app-nav {
  border-radius: 0 !important;
  border-left: none !important;
  border-right: none !important;
  border-top: none !important;
}

.app-logo {
  font-size: 1.2rem;
  font-weight: 700;
  cursor: pointer;
  user-select: none;
  margin-right: 1rem;
}

.app-content {
  flex: 1;
  padding: 1.5rem;
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
}

.nav-end {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.impersonation-banner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--p-yellow-100, #fef9c3);
  color: var(--p-yellow-800, #713f12);
  border: 1px solid var(--p-yellow-300, #fde047);
  border-radius: 6px;
  padding: 0.25rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 600;
}

@media (max-width: 640px) {
  .app-content {
    padding: 0.75rem;
  }

  .logo-text { display: none; }
  .view-as-select { display: none !important; }
  .impersonation-name { display: none; }

  .nav-account-btn :deep(.p-button-label),
  .nav-logout-btn :deep(.p-button-label) {
    display: none;
  }

  .impersonation-stop-btn :deep(.p-button-label) {
    display: none;
  }
}

/* Prevent horizontal overflow from causing wide modals */
body { overflow-x: hidden; }
</style>
